import Cldr from 'cldrjs';
import subtag from 'subtag';
import gql from 'graphql-tag';
import apollo from '../services/apollo';
import defaultMessages from '../translations/en-US.json';

// load bundled resources
// eslint-disable-next-line import/no-unresolved
import suplementalLikelyTags from '../translations/cldr-data/supplemental/likelySubtags';
// eslint-disable-next-line import/no-unresolved
import suplementalWeekData from '../translations/cldr-data/supplemental/weekData';
// eslint-disable-next-line import/no-unresolved
import suplementalTimeData from '../translations/cldr-data/supplemental/timeData';
// eslint-disable-next-line import/no-unresolved
import mainEnLanguages from '../translations/cldr-data/main/en/languages';

// static initialization for CLDR
Cldr.load(suplementalLikelyTags);
Cldr.load(suplementalWeekData);
Cldr.load(suplementalTimeData);
Cldr.load(mainEnLanguages);

const SET_SUPPORTED_LANGUAGES = 'SET_SUPPORTED_LANGUAGES';
const SET_CURRENT_LANGUAGE = 'SET_CURRENT_LANGUAGE';

const LOAD_CLDR_SUPPLEMENTAL = 'LOAD_CLDR_SUPPLEMENTAL';
const LOAD_CLDR_MAIN = 'LOAD_CLDR_MAIN';

const LOAD_LANGUAGE_TRANSLATIONS = 'LOAD_LANGUAGE_TRANSLATIONS';

const LOCALE_FETCH_ERROR = 'LOCALE_FETCH_ERROR';

/**
 * Convert timezone data to a suitable format for DropdownList.
 * { timezone: displayName, .. } => [ { value, group, name } ]
 * @param {*} tzData
 */
function dropdownFriendlyTimezones(flattenTimeZones) {
  return Object.keys(flattenTimeZones).map((zone) => {
    const group = zone.split('/')[0] || 'Etc';
    const label = flattenTimeZones[zone];

    return {
      zone,
      group,
      label,
    };
  });
}

function flattenTimeZone(ob) {
  const toReturn = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const i in ob) {
    if (Object.prototype.hasOwnProperty.call(ob, i)) {
      if (typeof ob[i] === 'object' && ob[i] !== null) {
        const flatObject = flattenTimeZone(ob[i]);
        // eslint-disable-next-line no-restricted-syntax
        for (const x in flatObject) {
          if (Object.prototype.hasOwnProperty.call(flatObject, x)) {
            if (x === 'exemplarCity') {
              toReturn[`${i}`] = flatObject[x];
            } else {
              toReturn[`${i}/${x}`] = flatObject[x];
            }
          }
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
  }
  return toReturn;
}

/**
 * Factory for enhanced Cldr object with methods to translate locale data.
 * @param {*} locale
 */
function buildCustomCldr(locale) {
  const cldr = new Cldr(locale);


  const teritoryDisplayName = (countryCode) => {
    const name = cldr.main(`localeDisplayNames/territories/${countryCode}`);
    if (!name) {
      return countryCode;
    }
    return name;
  };

  /**
   * Translsates a single language tag
   */
  cldr.localeDisplayName = (tag) => {
    let name = cldr.main(`localeDisplayNames/languages/${tag}`);

    if (!name) {
      const lang = subtag(tag);
      name = cldr.main(`localeDisplayNames/languages/${lang.language}`);
      if (!name) {
        name = tag;
      }
      const country = teritoryDisplayName(lang.region);
      name = `${name} (${country})`;
    }
    return name;
  };
  /**
   * Translates an array of language tags.
   */
  cldr.localeDisplayNames = (tags = []) => tags.sort().map(value => ({
    value,
    label: cldr.localeDisplayName(value),
  }));

  cldr.defaultTeritories = () => {
    const teritories = cldr.main('localeDisplayNames/territories');

    const codes = Object.keys(teritories).filter(value => !value.includes('-alt-'));

    return codes;
  };

  cldr.teritoryDisplayName = teritoryDisplayName;

  /**
   * Translated teritory/country display names
   */
  cldr.teritoryDisplayNames = (countryCodes = []) => countryCodes.sort().map(value => ({
    value,
    label: cldr.teritoryDisplayName(value),
  }));

  /**
   * Translated TimeZone name
   */
  cldr.translatedTimeZone = (timeZone, fallback) => {
    const name = cldr.main(`dates/timeZoneNames/zone/${timeZone}/exemplarCity`);
    if (!name) {
      if (!fallback) {
        return timeZone;
      }
      return fallback;
    }
    return name;
  };

  cldr.flattenTimezones = () => {
    const timeZoneHierarchy = cldr.main('dates/timeZoneNames/zone');
    return flattenTimeZone(timeZoneHierarchy);
  };

  return cldr;
}

const initialCldr = buildCustomCldr('en-US');

/**
 * Store to manage locale data:
 *
 * - the current locale, defaults to 'en-US' - defaults to navigator.language in browser
 * - available list of translations: loaded from file and refreshed from the server via XHR
 * - way to change the current locale from a list of available translations
 * - load the translation data for the selected language
 */
const initState = {
  /**
   * Current selected language
   */
  locale: 'en-US',
  /**
   * Loaded translations
   */
  messages: defaultMessages,
  /**
   * Available translations: key is language tag and messages are the translation.
   */
  availableTranslations: {
    en: defaultMessages,
  },
  /**
   * Available languages. User can select them to change the UI translation.
   * List is loaded from the server.
   * useLocale() should only allow switching to one of these languages.
   */
  supportedLanguages: ['en-US', 'ro'],

  /**
   * Localized locale display names for supported languages.
   */
  translatedTags: initialCldr.localeDisplayNames(['en-US', 'ro']),
  /**
   * Translated timezones to display to the user.
   */
  translatedTimeZones: [],
  /**
   * Cldr instance
   */
  cldr: initialCldr,

  isCldrSuplementalLoaded: false,
  /**
   * Object with keys that mark if a language was loaded
   */
  isCldrLanguageLoaded: [],
};

export const setSupportedLanguages = languages => ({
  type: SET_SUPPORTED_LANGUAGES,
  languages,
});

const setCurrentLanguage = locale => ({
  type: SET_CURRENT_LANGUAGE,
  locale,
});

/**
 * Update store isCldrSuplementalLoaded property to true.
 */
const loadCldrSupplemental = () => ({
  type: LOAD_CLDR_SUPPLEMENTAL,
});

const loadCldrMain = locale => ({
  type: LOAD_CLDR_MAIN,
  locale,
});

export const errorFetchingLocaleData = (error, locale) => ({
  type: LOCALE_FETCH_ERROR,
  error,
  locale,
});

const loadLanguageTranslations = (locale, messages) => ({
  type: LOAD_LANGUAGE_TRANSLATIONS,
  locale,
  messages,
});

/**
 * Given a list of URL's, fetches them.
 * Returns a list of data responses once all of them are loaded.
 * @param {*} urls
 */
export function fetchAllUrls(urls) {
  return Promise.all(urls.map(url => fetch(url).then(response => response.json())));
}

const supplementalCldrUrls = (baseUrl = '/components/cldr-data') => [
  `${baseUrl}/supplemental/likelySubtags.json`,
  `${baseUrl}/supplemental/weekData.json`,
  `${baseUrl}/supplemental/timeData.json`,
];

function fetchCldrSuplemental(baseUrl) {
  return fetchAllUrls(supplementalCldrUrls(baseUrl));
}

const mainCldrUrls = (locale, baseUrl = '/components/cldr-data') => [
  `${baseUrl}/main/${locale}/currencies.json`,
  `${baseUrl}/main/${locale}/languages.json`,
  `${baseUrl}/main/${locale}/territories.json`,
  `${baseUrl}/main/${locale}/scripts.json`,
  `${baseUrl}/main/${locale}/timeZoneNames.json`,
];

function fetchCldrMain(locale, baseUrl) {
  return fetchAllUrls(mainCldrUrls(locale, baseUrl));
}

/**
 * Fetch translations using a fallback:
 *
 * - try locale: e.g. pt-BR
 * - try locale language: e.g. pt
 * - default to American English: en-US
 *
 * @param {*} locale
 */
function fetchTranslations(locale) {
  return fetch(`/apps/client/translations/${locale}.json`)
    .then(response => response.json())
    .catch(() => {
      const langObj = subtag(locale);
      const languagePart = langObj.language;
      console.warn(`Fail to load messages for ${locale}, try to get the short name...`);
      const second = `/apps/client/translations/${languagePart}.json`;
      return fetch(second)
        .then(res => res.json())
        .catch(() => {
          console.warn(`Fail to load messages for ${languagePart}, try to get the default...`);
          const thirdUrl = '/apps/client/translations/en-US.json';
          return fetch(thirdUrl)
            .then(res => res.json())
            .catch(() => {
              console.warn('Fail to load the default language, fallback to the local');
              return Promise.resolve(defaultMessages);
            });
        });
    });
}

export const fetchSupportedLanguages = () => {
  const LOCALE_QUERY = gql`
    {
      languages: activeLocales
    }
  `;

  return apollo.query({ query: LOCALE_QUERY });
};

/**
 * Loads the locales and then builds a CLDR instance using the given locale.
 * TODO: Load available languages
 * TODO: Compile CLDR data and translations into a single file
 * TODO: Loading is faster and less error prone when we have a single file
 * @param {*} locale
 * @param {*} baseUrl
 */
export function useLocale(locale, baseUrl) {
  let fallbackLocale = locale;

  if (locale === 'pt-PT') {
    fallbackLocale = 'pt';
  }

  return function useLocaleInternal(dispatch, getState) {
    // load supplemental data only once
    const promisses = [];
    const state = getState();
    if (!state.i18n.isCldrSuplementalLoaded) {
      // load cldr supplementals
      promisses.push(
        fetchCldrSuplemental(baseUrl).then((data) => {
          Cldr.load(data);
          return dispatch(loadCldrSupplemental());
        }),
      );
    }
    if (!state.i18n.isCldrLanguageLoaded.includes(fallbackLocale)) {
      // load cldr main
      promisses.push(
        fetchCldrMain(fallbackLocale, baseUrl).then((data) => {
          Cldr.load(data);
          return dispatch(loadCldrMain(fallbackLocale));
        }),
      );
    }

    if (!state.i18n.availableTranslations[fallbackLocale]) {
      promisses.push(fetchTranslations(fallbackLocale)
        .then(data => dispatch(loadLanguageTranslations(fallbackLocale, data))));
      // load app translations
    }

    promisses.push(fetchSupportedLanguages().then(res => dispatch(setSupportedLanguages(res.data.languages))));

    // wait for the data to load and initialize the locale and translations
    return Promise.all(promisses)
      .then(() => dispatch(setCurrentLanguage(fallbackLocale)))
      .catch(error => dispatch(errorFetchingLocaleData(error, fallbackLocale)));
  };
}

function getTranslationsOrDefault(translations, locale) {
  return translations[locale] ? translations[locale] : translations.en;
}

const localeReducer = (state = initState, action) => {
  switch (action.type) {
    case SET_SUPPORTED_LANGUAGES: {
      return {
        ...state,
        supportedLanguages: action.languages,
      };
    }
    case SET_CURRENT_LANGUAGE: {
      const { locale } = action;
      const { supportedLanguages, availableTranslations } = state;
      // we change only to supported languages - remove true once we load the supported languages
      const messages = getTranslationsOrDefault(availableTranslations, locale);

      const cldr = buildCustomCldr(locale);
      const translatedTags = cldr.localeDisplayNames(supportedLanguages);
      const flattenTimeZones = cldr.flattenTimezones();
      const translatedTimeZones = dropdownFriendlyTimezones(flattenTimeZones);

      let newLocale = locale;
      if (!supportedLanguages.includes(locale)) {
        // we default to en-US if locale is not in the list of available translations
        newLocale = 'en-US';
      }
      return {
        ...state,
        locale: newLocale,
        cldr,
        messages,
        translatedTags,
        translatedTimeZones,
      };
    }
    case LOAD_CLDR_SUPPLEMENTAL:
      return {
        ...state,
        isCldrSuplementalLoaded: true,
      };
    case LOAD_CLDR_MAIN: {
      const { locale } = action;
      const { isCldrLanguageLoaded } = state;
      return {
        ...state,
        isCldrLanguageLoaded: [...isCldrLanguageLoaded, locale],
      };
    }
    case LOAD_LANGUAGE_TRANSLATIONS: {
      const availableTranslations = Object.assign({}, state.availableTranslations);
      availableTranslations[action.locale] = action.messages;
      return {
        ...state,
        messages: Object.assign({}, action.messages),
        availableTranslations,
      };
    }
    case LOCALE_FETCH_ERROR: {
      console.error(`Error fetching locale ${action.locale} - ${action.error}`);
      return state;
    }
    default:
      return state;
  }
};

export default localeReducer;
